import { useEffect, useState } from "react";
import { Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Target, X } from "lucide-react";
import {
  endFocus,
  focusProgress,
  formatClock,
  readFocus,
  subscribeFocus,
  type FocusSession,
} from "@/lib/focus-session";

/**
 * Barra fissa della modalità Focus: obiettivo della sessione + timer.
 * Resta visibile su tutte le pagine finché la sessione è attiva.
 */
export function FocusBar() {
  const { t } = useTranslation();
  const [session, setSession] = useState<FocusSession | null>(null);
  const [, tick] = useState(0);
  const [celebrated, setCelebrated] = useState(false);

  useEffect(() => {
    setSession(readFocus());
    return subscribeFocus(() => setSession(readFocus()));
  }, []);

  useEffect(() => {
    if (!session) return;
    const id = window.setInterval(() => tick((n) => n + 1), 1000);
    return () => window.clearInterval(id);
  }, [session]);

  const progress = session ? focusProgress(session) : null;

  useEffect(() => {
    if (!session || !progress?.done || celebrated) return;
    setCelebrated(true);
    toast.success(t("today.resume.focus.done", { min: session.minutes }));
  }, [session, progress?.done, celebrated, t]);

  if (!session || !progress) return null;

  function stop() {
    const min = endFocus();
    setSession(null);
    setCelebrated(false);
    toast(t("today.resume.focus.stopped", { min }));
  }

  return (
    <AnimatePresence>
      <motion.div
        key="focus-bar"
        initial={{ y: 24, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 24, opacity: 0 }}
        role="region"
        aria-label={t("today.resume.focus.aria")}
        className="fixed inset-x-0 bottom-[calc(env(safe-area-inset-bottom)+4.75rem)] z-40 px-3"
      >
        <div className="mx-auto flex max-w-md items-center gap-3 rounded-2xl border border-border/60 bg-background/95 p-3 shadow-[var(--shadow-soft)] backdrop-blur sm:max-w-2xl">
          <span className="rounded-xl bg-primary/10 p-2 text-primary">
            <Target aria-hidden="true" className="h-4 w-4" />
          </span>
          <Link to={session.to as never} className="min-w-0 flex-1">
            <span className="block truncate text-sm font-semibold">{session.label}</span>
            <span className="block truncate text-xs text-muted-foreground">{session.goal}</span>
            <span
              role="progressbar"
              aria-valuemin={0}
              aria-valuemax={100}
              aria-valuenow={Math.round(progress.ratio * 100)}
              aria-valuetext={formatClock(progress.remainingSec)}
              aria-label={t("today.resume.focus.aria")}
              className="mt-1.5 block h-1.5 w-full overflow-hidden rounded-full bg-muted"
            >
              <span
                className="block h-full rounded-full bg-primary transition-[width] duration-500"
                style={{ width: `${progress.ratio * 100}%` }}
              />
            </span>
          </Link>
          <span className="shrink-0 tabular-nums text-sm font-semibold">
            {formatClock(progress.remainingSec)}
          </span>
          <Button
            size="icon"
            variant="ghost"
            className="shrink-0 rounded-full"
            onClick={stop}
            aria-label={t("today.resume.focus.stop")}
          >
            <X className="h-4 w-4" aria-hidden="true" />
          </Button>
        </div>
      </motion.div>
    </AnimatePresence>
  );
}
